import React, { useCallback, useMemo, useRef, useState } from 'react';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { autocompletion } from '@codemirror/autocomplete';
import { promptEditorTheme, promptEditorDarkOverrides } from './prompt-editor-theme';
import { refHighlightExtension } from './prompt-editor-ref-extension';
import { refAutocompleteSource, type RefSuggestion } from './prompt-editor-ref-autocomplete';
import { MarkdownRenderer } from './MarkdownRenderer';

export type { RefSuggestion } from './prompt-editor-ref-autocomplete';

export interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  className?: string;
  error?: boolean;
  readOnly?: boolean;
  highlightRefs?: boolean;
  enableAutocomplete?: boolean;
  suggestions?: RefSuggestion[];
  onInsertRef?: () => void;
}

type ViewMode = 'source' | 'preview' | 'split';

export const PromptEditor: React.FC<PromptEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter the system prompt...',
  minHeight = '200px',
  maxHeight = '500px',
  className,
  error = false,
  readOnly = false,
  highlightRefs = true,
  enableAutocomplete = false,
  suggestions = [],
  onInsertRef,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('source');
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  const extensions = useMemo(() => {
    const exts = [
      markdown({ codeLanguages: languages }),
      promptEditorTheme,
      promptEditorDarkOverrides,
    ];
    if (highlightRefs) {
      exts.push(refHighlightExtension);
    }
    if (enableAutocomplete && suggestions.length > 0) {
      exts.push(
        autocompletion({
          override: [refAutocompleteSource(suggestions)],
        })
      );
    }
    return exts;
  }, [highlightRefs, enableAutocomplete, suggestions]);

  const handleChange = useCallback(
    (val: string) => {
      onChange(val);
    },
    [onChange]
  );

  const borderClass = error
    ? 'border-destructive'
    : 'border-input focus-within:ring-1 focus-within:ring-ring';

  const showEditor = viewMode === 'source' || viewMode === 'split';
  const showPreview = viewMode === 'preview' || viewMode === 'split';

  return (
    <div className={`rounded-md border ${borderClass} bg-background overflow-hidden ${className ?? ''}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b px-2 py-1 bg-muted/50">
        <div className="flex items-center gap-1">
          {(['source', 'preview', 'split'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === mode
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
        {onInsertRef && (
          <button
            type="button"
            onClick={onInsertRef}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground rounded transition-colors"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5V19A9 3 0 0 0 21 19V5" />
              <path d="M3 12A9 3 0 0 0 21 12" />
            </svg>
            Insert Reference
          </button>
        )}
      </div>

      {/* Editor / Preview Area */}
      <div
        className={`${viewMode === 'split' ? 'grid grid-cols-2 divide-x' : ''}`}
        style={{ minHeight, maxHeight }}
      >
        {showEditor && (
          <div className="overflow-auto" style={{ minHeight, maxHeight }}>
            <CodeMirror
              ref={editorRef}
              value={value}
              onChange={handleChange}
              extensions={extensions}
              placeholder={placeholder}
              readOnly={readOnly}
              basicSetup={{
                lineNumbers: false,
                foldGutter: false,
                highlightActiveLine: true,
                bracketMatching: false,
                indentOnInput: true,
              }}
              style={{ minHeight, maxHeight, fontSize: '14px' }}
            />
          </div>
        )}
        {showPreview && (
          <div
            className="overflow-auto p-4"
            style={{ minHeight, maxHeight }}
          >
            {value ? (
              <MarkdownRenderer content={value} />
            ) : (
              <p className="text-muted-foreground text-sm italic">
                Nothing to preview
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
